import { AnyAction } from 'redux';
import { opApiRequest } from 'src/api/apiRequest';
import endpoints from 'src/api/opEndpoints';
import apiTypes from 'src/api/optimalprint-sdk.d';
import isDesignSaving from 'src/store/design/selector/isDesignSaving';
import { DesignData } from 'src/store/design/types';
import { Store } from 'src/store/index';
import { ThunkDispatch } from 'redux-thunk';
import getDesignContentHash from 'src/util/design/getDesignContentHash';
import useIntegrationLayer from 'src/util/hook/useIntegrationLayer';
import {
  setDesignIsSavingAction,
  setDesignIdAction,
  setDesignIsPublicAction,
  setDesignIsLoadedAction, setEncryptedDesignIdAction,
} from 'src/store/design/slice';
import fetchPricesOperation from 'src/store/price/operation/fetchPricesOperation';
import { wallpaperProductTypeIds } from 'src/api/productTypes';
import getIncludeDpiEnhancement from 'src/store/design/selector/getIncludeDpiEnhancement';
import getIncludeCanvasHangerKit from 'src/store/design/selector/getIncludeCanvasHangerKit';
import getIncludeRemoveLogo from 'src/store/upsell/selector/getIncludeRemoveLogo';
import ensureCorrectProductUid from 'src/util/design/ensureCorrectProductUid';
import resolveBoundProduct from 'src/util/design/resolveBoundProduct';
import getAbstractProduct from '../selector/getAbstractProduct';
import getProductId from '../selector/getProductId';
import getQuantity from '../selector/getQuantity';

const addCustomerDesignOperation = (categoryId: number, productTypeId: number, structure: DesignData, encPublicDesignId?: string, productId?: number) => async (
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
  getState: () => Store,
) => {
  const store = getState();
  if (isDesignSaving(store)) {
    return;
  }
  const {
    clientPlatform,
    productTypeId,
  } = useIntegrationLayer();
  const isWallpaperProduct = wallpaperProductTypeIds.indexOf(productTypeId) !== -1;
  const quantity = getQuantity(store);

  // Ensure the correct product_uid is set based on populated spreads
  const structureToSave = ensureCorrectProductUid(structure);

  // Bind the same variant the uid resolves to, so the design starts out on the
  // product it will be saved and priced as (see resolveBoundProduct).
  const productId = resolveBoundProduct(getAbstractProduct(store), structureToSave)?.productId || getProductId(store) || 1;

  dispatch(setDesignIsSavingAction(true));
  let response;
  try {
    response = (await opApiRequest(
      endpoints.CUSTOMER_DESIGN_V3_ADD,
      {
        encPublicDesignId,
        categoryId,
        productId,
        structure: structureToSave,
        isDesignCompleted: true,
        clientPlatform,
        contentHash: getDesignContentHash(structureToSave),
      },
      'data',
      'POST',
    )) as apiTypes.AppBundle.Api.Response.Customer.Design.Design.V3.CustomerDesignAddV3Response;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Cannot add customer design, exception: ', e);
  }

  if (!response) {
    // eslint-disable-next-line no-console
    console.error('Cannot add customer design');
  }

  if (response) {
    dispatch(setDesignIdAction(response.customerDesignId));
    dispatch(setEncryptedDesignIdAction(response.encryptedDesignId));
    const upscale = getIncludeDpiEnhancement(store);
    const hangerKit = getIncludeCanvasHangerKit(store);
    const removeLogo = getIncludeRemoveLogo(store);
    const upscaleParam = upscale ? '&upscale=1' : '';
    const hangerKitParam = hangerKit ? '&hangerKit=1' : '';
    const removeLogoParam = removeLogo ? '&removeLogo=1' : '';
    // eslint-disable-next-line no-restricted-globals
    history.replaceState({}, '', `/designer?designId=${response.customerDesignId}&qty=${quantity}${upscaleParam}${hangerKitParam}${removeLogoParam}`);
    dispatch(setDesignIsPublicAction(false));
    dispatch(setDesignIsLoadedAction(true));
    isWallpaperProduct && dispatch(fetchPricesOperation(categoryId, [productId], true));
  }

  dispatch(setDesignIsSavingAction(false));
};

export default addCustomerDesignOperation;
